import axios from 'axios';
import cn from 'classnames';
import { User } from 'common/interfaces/api';
import { Button } from 'components/v2/atoms/button';
import {
  BellActiveIcon,
  BellMutedIcon,
  ChevronDownIcon,
  UnfollowIcon,
} from 'components/v2/atoms/icon';
import { useCallback, useEffect, useRef, useState } from 'react';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

interface FollowButtonPropsType {
  user: User;
}

const FollowButton = ({ user }: FollowButtonPropsType) => {
  const currentUser = useStore((state) => state.currentUser);
  const refetchCurrentProfile = useStore(
    (state) => state.refetchCurrentProfile,
  );
  const refetchPosts = useStore((state) => state.refetchPosts);
  const showLogin = useGlobalStore((state) => state.showLogin);

  const [isFollowed, setIsFollowed] = useState(user?.is_followed);
  const [isNotificationOn, setIsNotificationOn] = useState(
    !!user?.get_notifications_type,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsFollowed(user?.is_followed);
  }, [user?.is_followed]);

  useEffect(() => {
    setIsNotificationOn(!!user?.get_notifications_type);
  }, [user?.get_notifications_type]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFollowClick = useCallback(async () => {
    if (!currentUser) {
      showLogin({});
      return;
    }

    setIsFollowed(true);

    try {
      await axios.post(`/users/follows`, {
        user_id: user.id,
        action: 'follow',
      });
      refetchCurrentProfile();
      refetchPosts();
    } catch (error) {
      setIsFollowed(false);
      console.error('Failed to follow:', error);
    }
  }, [currentUser, user?.id, showLogin, refetchCurrentProfile, refetchPosts]);

  const handleUnfollow = useCallback(async () => {
    if (!currentUser) {
      showLogin({});
      return;
    }

    setIsFollowed(false);
    setIsDropdownOpen(false);

    try {
      await axios.post(`/users/follows`, {
        user_id: user.id,
        action: 'unfollow',
      });
      refetchCurrentProfile();
      refetchPosts();
    } catch (error) {
      setIsFollowed(true);
      console.error('Failed to unfollow:', error);
    }
  }, [currentUser, user?.id, showLogin, refetchCurrentProfile, refetchPosts]);

  const handleNotificationToggle = useCallback(
    async (turnOn: boolean) => {
      if (!currentUser) {
        showLogin({});
        return;
      }

      const newType = turnOn ? 'post' : '';
      setIsNotificationOn(turnOn);
      setIsDropdownOpen(false);

      try {
        await axios.post(`/profiles/followership_notification`, {
          followee_name: user.username,
          notification_type: newType,
        });
        refetchCurrentProfile();
      } catch (error) {
        setIsNotificationOn(!turnOn);
        console.error('Failed to toggle notification:', error);
      }
    },
    [currentUser, user?.username, showLogin, refetchCurrentProfile],
  );

  // If not following, show simple follow button
  if (!isFollowed) {
    return (
      <Button
        variant="blackDefault"
        size="md"
        className="min-w-[100px] rounded-full border border-pp-border-primary font-bold"
        onClick={handleFollowClick}
      >
        フォロー
      </Button>
    );
  }

  // If following, show button with bell icon and dropdown
  return (
    <div ref={dropdownRef} className="relative">
      {/* Main Button with Bell Icon and Dropdown Arrow */}
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={cn(
          'flex h-9 items-center gap-1 rounded-full border border-solid border-pp-border-primary px-3 transition-colors',
          'bg-white text-pp-text-primary hover:bg-pp-bg-secondary',
        )}
      >
        {isNotificationOn ? (
          <BellActiveIcon className="size-5" />
        ) : (
          <BellMutedIcon className="size-5" />
        )}
        <ChevronDownIcon
          className={cn('size-4 transition-transform', {
            'rotate-180': isDropdownOpen,
          })}
        />
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-lg border border-pp-border-primary bg-white shadow-lg">
          {/* Notification ON Option */}
          <button
            onClick={() => handleNotificationToggle(true)}
            className={cn(
              'flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-pp-bg-secondary',
              {
                'bg-pp-bg-secondary': isNotificationOn,
              },
            )}
          >
            <BellActiveIcon className="size-5 shrink-0 text-pp-text-primary" />
            <span className="text-pp-text-primary">通知ON</span>
          </button>

          {/* Notification OFF Option */}
          <button
            onClick={() => handleNotificationToggle(false)}
            className={cn(
              'flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-pp-bg-secondary',
              {
                'bg-pp-bg-secondary': !isNotificationOn,
              },
            )}
          >
            <BellMutedIcon className="size-5 shrink-0 text-pp-text-primary" />
            <span className="text-pp-text-primary">通知OFF</span>
          </button>

          {/* Divider */}
          <div className="border-t border-pp-border-primary" />

          {/* Unfollow Option */}
          <button
            onClick={handleUnfollow}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-pp-bg-secondary"
          >
            <UnfollowIcon className="size-5 shrink-0 text-pp-text-primary" />
            <span className="text-pp-text-primary">フォローを解除</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default FollowButton;
